import { CheckCircle, WarningCircle } from "@phosphor-icons/react"
import { Field } from "@tokenterminal/core/ui/input/Field"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import Link from "next/link"
import { useMemo } from "react"
import { type ChainStatusItem } from "../../types/ApiTypes"
import {
  formatTimestampToRelative,
  formatTimestampUTCDate,
} from "../../utils/date"
import { useChainStatus } from "./hooks/useChainStatus"

interface ChainStatusFieldProps {
  chainApiId: string
}

export function ChainStatusField({ chainApiId }: ChainStatusFieldProps) {
  const { data, isLoading, error } = useChainStatus()

  const chainStatus = useMemo(() => {
    if (!data?.data) return null
    // Try to match by slug first, then fallback to api_id for backward compatibility
    return (
      data.data.find((chain: ChainStatusItem) => chain.slug === chainApiId) ||
      data.data.find((chain: ChainStatusItem) => chain.api_id === chainApiId)
    )
  }, [data?.data, chainApiId])

  // Don't render if loading, error, or no status found
  if (isLoading || error || !chainStatus) {
    return null
  }

  const formattedTimestamp = formatTimestampUTCDate(
    chainStatus.last_approved_timestamp
  )
  const relativeTimestamp = formatTimestampToRelative(
    chainStatus.last_approved_timestamp
  )

  const StatusIcon = chainStatus.is_fresh ? CheckCircle : WarningCircle
  const statusColor = chainStatus.is_fresh
    ? "text-green-500"
    : "text-yellow-500"
  const statusText = chainStatus.is_fresh ? "Fresh" : "Stale"
  const badgeColor = chainStatus.is_fresh ? "green" : "yellow"

  return (
    <Field>
      <Link
        href={`/chain-status/`}
        className="block focus:outline-none focus:ring-2 focus:ring-primary-600 rounded transition hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5 p-1"
        tabIndex={0}
        aria-label={`View chain status details for ${chainStatus.api_id}`}
      >
        <div className="flex items-center gap-2">
          <Badge
            color={badgeColor}
            variant="muted"
            className="inline-flex items-center gap-1"
            size="small"
          >
            <StatusIcon size={12} />
            {statusText}
          </Badge>
          <span className="text-xs text-fg-secondary">
            Latest: {formattedTimestamp}
          </span>
        </div>
      </Link>
    </Field>
  )
}
